import React, { useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import { IoIosArrowDown } from "react-icons/io";
import { useTranslation } from "react-i18next";
import {
  FaBolt,
  FaCamera,
  FaCar,
  FaCat,
  FaClock,
  FaCode,
  FaCoffee,
  FaDumbbell,
  FaFutbol,
  FaGamepad,
  FaHeadphones,
  FaHorse,
  FaLaptop,
  FaMobileAlt,
  FaMountain,
  FaMugHot,
  FaMusic,
  FaPizzaSlice,
  FaSeedling,
  FaServer,
  FaTree,
} from "react-icons/fa";
import { MdOutdoorGrill } from "react-icons/md";
import { GiCowboyBoot } from "react-icons/gi";
import {
  FaGithubIcon,
  FaInstagramIcon,
  FaLinkedinIcon,
  FaWhatsappIcon,
} from "../components/Icons";
import TechTags from "../components/TechTags";
import GitHubContributions from "../components/GitHubContributions";
import useReveal from "../hooks/useReveal";

/* ============================================================
   LAYOUT BASE
   ============================================================ */
const Wraper = styled.div`
  background: var(--bg);
  width: 100%;
  overflow-x: clip;
`;

const DivPrincipal = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  overflow-x: clip;
  height: auto;
`;

const DivMargin = styled.div`
  height: 1px;
  scroll-margin-top: 96px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--line);
  scroll-margin-top: 96px;
`;

/* ============================================================
   HERO
   ============================================================ */
const DivText = styled.section`
  position: relative;
  z-index: 2;
  width: 100%;
  min-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: visible;

  @media (max-width: 900px) {
    min-height: 100svh;
  }
`;

const HeroParallaxLayer = styled.div`
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;

  @media (prefers-reduced-motion: reduce) {
    will-change: auto;
    transform: none !important;
  }
`;

const HeroDotsLayer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: calc(-1 * clamp(48px, 10vh, 96px));
  z-index: 0;
  overflow: hidden;
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    height: clamp(96px, 22vh, 180px);
    background: linear-gradient(
      to bottom,
      transparent 0%,
      color-mix(in srgb, var(--bg) 55%, transparent) 45%,
      var(--bg) 100%
    );
    pointer-events: none;
  }
`;

const HeroSnippet = styled.pre`
  display: none;
  position: absolute;
  top: clamp(96px, 12vh, 128px);
  right: var(--container-x);
  z-index: 1;
  margin: 0;
  font-family: var(--font-mono);
  font-size: clamp(11px, 1.25vw, 13px);
  font-weight: 500;
  line-height: 1.55;
  letter-spacing: -0.02em;
  color: var(--text-3);
  pointer-events: none;
  user-select: none;
  white-space: pre;
  padding: 18px 22px;
  border: 1px solid var(--line);
  background: var(--bg);
  /* Papel retangular, com uma inclinação leve em torno do alfinete. */
  transform-origin: 28px 0;
  transform: rotate(2deg);
  /* Camada seca: parte de zero e se abre em direção ao canto solto. */
  .paper-depth {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% + 8px);
    height: calc(100% + 9px);
    background: var(--accent);
    clip-path: polygon(
      calc(100% - 8px) 0,
      100% 100%,
      0 calc(100% - 9px),
      calc(100% - 8px) calc(100% - 9px)
    );
  }

  /* Alfinete: haste afilada atrás da cabeça, preso à borda do bilhete. */
  &::before {
    content: "";
    position: absolute;
    top: -13px;
    left: 26.5px;
    width: 3px;
    height: 25px;
    background: var(--ink);
    clip-path: polygon(0 0, 100% 0, 50% 100%);
    transform: rotate(24deg);
    transform-origin: top center;
  }

  &::after {
    content: "";
    position: absolute;
    top: -21px;
    left: 20px;
    width: 13px;
    height: 13px;
    border: 1.5px solid var(--ink);
    border-radius: 50%;
    background: var(--accent);
    box-shadow: inset 2px 2px 0 rgb(255 255 255 / 45%), 2px 2px 0 rgb(0 0 0 / 18%);
  }

  .kw {
    color: var(--text-2);
  }

  .key {
    color: var(--text-2);
  }

  .str {
    color: var(--accent-text);
  }

  .bool {
    color: var(--accent-text);
  }

  .punct {
    color: var(--text-3);
  }

  .cmt {
    color: var(--text-3);
    opacity: 0.75;
  }

  @media (min-width: 900px) {
    display: block;
  }
`;

const HeroInner = styled(HeroParallaxLayer)`
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  max-width: var(--container-max);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 28px;
  padding: clamp(108px, 14vh, 140px) var(--container-x) clamp(64px, 10vh, 100px);
  box-sizing: border-box;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 40px;
  }

  @media (max-width: 899px) {
    padding: 112px var(--container-x) 56px;
  }
`;

const HeroActions = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
    gap: 14px;
  }
`;

/* Campo de pontos interativo do hero:
   os pontos fogem do cursor e voltam suave à origem ao afastar. */
const HeroCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
`;

const HeroDotField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const motionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    let prefersReduced = motionQuery.matches;

    const SPACING = 24; // mesmo espaçamento do grid CSS anterior
    const DOT_RADIUS = 1.1;
    const INFLUENCE = 130; // raio de ação do cursor (px)
    const MAX_PUSH = 28; // deslocamento máximo por ponto (px)
    const EASE = 0.15; // suavização do retorno
    // BASE = --line-soft (ponto em repouso), NEAR = --accent (ponto sob o cursor).
    let BASE = { r: 226, g: 226, b: 226 };
    let NEAR = { r: 196, g: 92, b: 38 };

    const hexToRgb = (hex) => {
      const v = (hex || "").trim().replace("#", "");
      if (v.length === 3) {
        return {
          r: Number.parseInt(v[0] + v[0], 16),
          g: Number.parseInt(v[1] + v[1], 16),
          b: Number.parseInt(v[2] + v[2], 16),
        };
      }
      if (v.length >= 6) {
        return {
          r: Number.parseInt(v.slice(0, 2), 16),
          g: Number.parseInt(v.slice(2, 4), 16),
          b: Number.parseInt(v.slice(4, 6), 16),
        };
      }
      return null;
    };

    const readThemeColors = () => {
      const cs = getComputedStyle(document.documentElement);
      BASE = hexToRgb(cs.getPropertyValue("--line-soft")) || BASE;
      NEAR = hexToRgb(cs.getPropertyValue("--accent")) || NEAR;
    };

    readThemeColors();

    let dots = [];
    let width = 0;
    let height = 0;
    let dpr = 1;
    let rafId = null;
    const mouse = { x: -9999, y: -9999, active: false };

    const buildDots = () => {
      dots = [];
      const cols = Math.ceil(width / SPACING);
      const rows = Math.ceil(height / SPACING);
      const offsetX = (width - cols * SPACING) / 2 + SPACING / 2;
      const offsetY = (height - rows * SPACING) / 2 + SPACING / 2;
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          const hx = offsetX + c * SPACING;
          const hy = offsetY + r * SPACING;
          dots.push({ hx, hy, x: hx, y: hy });
        }
      }
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = `rgb(${BASE.r}, ${BASE.g}, ${BASE.b})`;
      for (const d of dots) {
        ctx.beginPath();
        ctx.arc(d.hx, d.hy, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      let moving = false;

      for (const d of dots) {
        let tx = d.hx;
        let ty = d.hy;
        let strength = 0;

        if (mouse.active) {
          const dx = d.hx - mouse.x;
          const dy = d.hy - mouse.y;
          const dist = Math.hypot(dx, dy);
          if (dist < INFLUENCE && dist > 0.0001) {
            strength = 1 - dist / INFLUENCE;
            const push = strength * MAX_PUSH;
            tx = d.hx + (dx / dist) * push;
            ty = d.hy + (dy / dist) * push;
          }
        }

        d.x += (tx - d.x) * EASE;
        d.y += (ty - d.y) * EASE;

        if (Math.abs(tx - d.x) > 0.1 || Math.abs(ty - d.y) > 0.1) {
          moving = true;
        }

        const r = Math.round(BASE.r + (NEAR.r - BASE.r) * strength);
        const g = Math.round(BASE.g + (NEAR.g - BASE.g) * strength);
        const b = Math.round(BASE.b + (NEAR.b - BASE.b) * strength);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = moving ? requestAnimationFrame(draw) : null;
    };

    const requestTick = () => {
      if (rafId == null) rafId = requestAnimationFrame(draw);
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      if (!width || !height) return;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildDots();
      if (prefersReduced) drawStatic();
      else requestTick();
    };

    const onMove = (e) => {
      if (prefersReduced) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouse.active = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
      if (mouse.active) {
        mouse.x = x;
        mouse.y = y;
      }
      requestTick();
    };

    resize();

    const onMotionChange = () => {
      prefersReduced = motionQuery.matches;
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = null;
      mouse.active = false;
      buildDots();
      drawStatic();
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    motionQuery.addEventListener("change", onMotionChange);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Reage à troca de tema: recarrega as cores e redesenha.
    const themeObserver = new MutationObserver(() => {
      readThemeColors();
      if (prefersReduced) drawStatic();
      else requestTick();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => {
      window.removeEventListener("mousemove", onMove);
      motionQuery.removeEventListener("change", onMotionChange);
      ro.disconnect();
      themeObserver.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return <HeroCanvas ref={canvasRef} aria-hidden="true" />;
};

/* Ghost tipográfico: camada de fundo da cena. O H1 sólido é o sujeito. */
const HeroWatermark = styled(HeroParallaxLayer)`
  position: absolute;
  left: var(--container-x);
  bottom: 2%;
  z-index: 0;
  font-family: var(--font-display);
  font-weight: 900;
  font-size: clamp(72px, 18vw, 240px);
  line-height: 0.8;
  letter-spacing: -0.04em;
  color: var(--watermark);
  pointer-events: none;
  user-select: none;
  white-space: normal;
  max-width: min(100%, calc(100% - var(--container-x) * 2));
`;

const HeroCopy = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  max-width: 100%;
`;

const HeroLead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const HeroPrint = styled.pre`
  position: relative;
  z-index: 1;
  margin: 0;
  padding: 0;
  font-family: var(--font-mono);
  font-size: clamp(12px, 2.4vw, 15px);
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: var(--text-3);
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .obj {
    color: var(--text-2);
  }

  .fn {
    color: var(--ink);
  }

  .str {
    color: var(--accent-text);
  }

  .punct {
    color: var(--text-3);
  }
`;

const TextFirst = styled.h1`
  position: relative;
  z-index: 1;
  margin: 0;
  width: 100%;
  font-family: var(--font-display);
  font-weight: 900;
  font-size: clamp(72px, 12vw, 90px);
  line-height: 0.88;
  letter-spacing: -0.04em;
  color: var(--ink);
  text-wrap: balance;

  &::after {
    content: "";
    display: block;
    width: 1.1em;
    height: 0.07em;
    margin-top: 0.18em;
    background: var(--accent);
    border-radius: 60% 20% 45% 15%;
    transform: rotate(-4deg);
  }

  @media (max-width: 899px) {
    font-size: clamp(48px, 13.5vw, 68px);
  }
`;

const TextSecond = styled.p`
  position: relative;
  z-index: 1;
  margin: 0;
  max-width: 32ch;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: clamp(16px, 2vw, 20px);
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: var(--text-2);
`;

const HeroMeta = styled.p`
  position: relative;
  z-index: 1;
  margin: 6px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-2);
  padding: 7px 10px;
  border: 1px solid var(--line);
  background: var(--bg);
  box-shadow: 3px 3px 0 var(--accent);
  transform: rotate(-2deg);

  img {
    width: 18px;
    height: 12px;
    object-fit: cover;
    border: 1px solid var(--line-soft);
    flex-shrink: 0;
  }
`;

const DivIcons = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border: 1px solid var(--line);
    background: var(--bg);
    color: var(--ink);
    box-shadow: 2px 2px 0 var(--ink);
    transition: background-color 0.2s ease, transform 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out);
  }

  a:hover, a:focus-visible {
    background: var(--accent-soft);
    transform: translate(-2px, -2px) rotate(-3deg);
    box-shadow: 4px 4px 0 var(--accent);
  }

  a:nth-child(even):hover {
    transform: translate(-2px, -2px) rotate(3deg);
  }

  a:active {
    transform: translate(2px, 2px);
    box-shadow: 0 0 0 var(--ink);
  }

  @media (max-width: 600px) {
    gap: 8px;
    a {
      width: 44px;
      height: 44px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    a:hover, a:nth-child(even):hover, a:focus-visible, a:active {
      transform: none;
    }
  }
`;

/* Botão Currículo: primary */
const ButtonCVContainer = styled.div`
  position: relative;
  z-index: 5;
  display: inline-flex;
  align-items: center;
`;

const ButtonCV = styled.button`
  background: var(--accent);
  border: 2px solid var(--ink);
  border-radius: 0;
  color: var(--on-accent);
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0 22px;
  height: 46px;
  min-height: 46px;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 4px 4px 0 0 var(--ink);
  transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
    border-color 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out),
    transform 0.15s var(--ease-out);

  [data-theme="dark"] & {
    color: var(--on-accent);
  }

  &:hover {
    background: var(--bg);
    color: var(--accent);
    border-color: var(--ink);
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 0 var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  &:active {
    transform: translate(3px, 3px);
    box-shadow: 1px 1px 0 var(--ink);
  }

  @media (max-width: 600px) {
    height: 44px;
    min-height: 44px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
      box-shadow 0.2s var(--ease-out);

    &:hover, &:active {
      transform: none;
    }
  }
`;

const ArrowIcon = styled(IoIosArrowDown)`
  font-size: 16px;
  color: currentColor;
  transition: transform 0.3s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 0;
  min-width: 180px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  transform: ${({ $isOpen }) => ($isOpen ? "translateY(0)" : "translateY(-8px)")};
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  z-index: 20;
  box-shadow: 4px 4px 0 var(--ink);

  @media (min-width: 900px) {
    left: auto;
    right: 0;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 13px 16px;
  border: none;
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--hover);
  }

  &:first-child {
    border-bottom: 1px solid var(--line-soft);
  }
`;

/* ============================================================
   SEÇÕES DE CONTEÚDO — títulos + onda accent (ref curta)
   ============================================================ */
const WAVE_MASK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 12' preserveAspectRatio='none'%3E%3Cpath d='M0 7 Q10 2 20 7 T40 7 T60 7 T80 7 T100 7 T120 7' fill='none' stroke='%23000' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

const sectionTitle = css`
  position: relative;
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  margin: 0 0 40px;
  padding-bottom: 0.42em;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(28px, 5vw, 46px);
  line-height: 1.02;
  letter-spacing: -0.04em;
  color: var(--ink);
  text-wrap: balance;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0.02em;
    width: 2.75em;
    max-width: 72px;
    height: 0.22em;
    min-height: 7px;
    pointer-events: none;
    background-color: var(--accent);
    -webkit-mask-image: ${WAVE_MASK};
    mask-image: ${WAVE_MASK};
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
  }

  @media (min-width: 900px) {
    margin-bottom: 56px;
  }
`;

const SectionProfile = styled.section`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  scroll-margin-top: 96px;
`;

const AboutSection = styled.section`
  width: 100%;
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--section-y) var(--container-x);
  box-sizing: border-box;
  color: var(--text);
  position: relative;

z-index: 8;
`;

const AboutContent = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
`;

const AboutBlocks = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: clamp(40px, 6vw, 64px);

  @media (min-width: 900px) {
    gap: clamp(48px, 6vw, 72px);
  }
`;

const AboutBlock = styled.div`
  position: relative;
  text-align: left;
  scroll-margin-top: 96px;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  ${({ $withLifeField }) =>
    $withLifeField &&
    css`
      overflow: hidden;
      min-height: 280px;
      padding-bottom: var(--space-xl);

      @media (min-width: 900px) {
        display: grid;
        grid-template-columns: minmax(0, 48ch) minmax(160px, 1fr);
        column-gap: clamp(32px, 5vw, 64px);
        align-items: start;
      }
    `}

  ${({ $spaceBefore }) =>
    $spaceBefore &&
    css`
      margin-top: clamp(32px, 7vw, 56px);
      padding-top: clamp(32px, 7vw, 56px);
      border-top: 1px solid var(--line-soft);

      @media (min-width: 900px) {
        margin-top: clamp(40px, 6vw, 80px);
        padding-top: clamp(40px, 6vw, 72px);
      }
    `}
`;

const AboutSectionTitle = styled.h2`
  ${sectionTitle}
  position: relative;
  z-index: 1;

  ${({ $span }) =>
    $span &&
    css`
      @media (min-width: 900px) {
        grid-column: 1 / -1;
      }
    `}
`;

const AboutIconFieldRoot = styled.div`
  display: grid;
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  color: var(--ink);
  opacity: 0.14;
  pointer-events: none;
  user-select: none;
`;

const AboutIconCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  svg {
    width: 13px;
    height: 13px;
  }
`;

const AboutMedia = styled.div`
  display: flex;
  position: relative;
  width: min(100%, 280px);
  aspect-ratio: 1 / 1;
  align-self: center;
  align-items: center;
  justify-content: center;
  margin-top: var(--space-xl);

  @media (min-width: 900px) {
    width: min(100%, 360px);
    margin-top: 0;
    margin-left: auto;
    grid-column: 2;
    grid-row: 2;
  }
`;

const AboutPortrait = styled.div`
  position: relative;
  z-index: 1;
  width: 72%;
  aspect-ratio: 1 / 1;
  border: 1px solid var(--line);
  background: var(--bg-2);
  overflow: hidden;
  box-shadow: 8px 8px 0 0 var(--accent);
  border-radius: 2px 14px 2px 2px;
  transform: rotate(-2deg);
  transition: transform 0.3s var(--ease-out), box-shadow 0.3s var(--ease-out);

  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    &:hover {
      transform: rotate(0deg) translate(-2px, -2px);
      box-shadow: 10px 10px 0 var(--accent);
    }
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Face / boné; corta espelho, placa e celular. */
    object-position: 50% 22%;
  }
`;

const AboutCopy = styled.div`
  position: relative;
  z-index: 1;
  max-width: 48ch;
  padding: var(--space-md) var(--space-lg);
  background: transparent;

  @media (min-width: 900px) {
    grid-column: 1;
    grid-row: 2;
    padding: var(--space-md) var(--space-xl);
  }
`;

const AboutSectionText = styled.p`
  font-size: clamp(15px, 1.35vw, 17px);
  line-height: 1.75;
  color: var(--text-2);
  font-family: var(--font-body);
  margin: 0 0 20px;
  max-width: 48ch;
  text-wrap: pretty;

  &:last-child {
    margin-bottom: 0;
  }
`;

/* ============================================================
   EXPERIÊNCIA
   ============================================================ */
const ExperienceTitle = styled.h3`
  ${sectionTitle}
`;

const CompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
  margin: 0 0 var(--space-md);
`;

const CompanyLogo = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
  border: 1px solid var(--line);
  padding: var(--space-xs);
  background: var(--bg);
`;

const CompanyName = styled.h3`
  font-size: clamp(24px, 3vw, 32px);
  margin: 0;
  font-family: var(--font-display);
  font-weight: 800;
  color: var(--ink);
  text-transform: lowercase;
  letter-spacing: -0.03em;
`;

const CompanyDescription = styled.p`
  margin: 0 0 var(--space-xl);
  max-width: 62ch;
  font-size: 15px;
  line-height: 1.75;
  color: var(--text-2);
  font-family: var(--font-body);
  text-wrap: pretty;
`;

const ExperienceTimeline = styled.div`
  --exp-pad: var(--space-lg);
  position: relative;
  width: 100%;
  margin: 0;
  padding-left: var(--exp-pad);
  border-left: 1px solid var(--line-soft);
  box-sizing: border-box;

  @media (max-width: 768px) {
    --exp-pad: var(--space-md);
  }
`;

const ExperienceTimelineItem = styled.div`
  position: relative;
  margin-bottom: var(--space-xl);

  /* Nó sólido ancorado no eixo da timeline (minimal-brutal, radius 0) */
  &::before {
    content: "";
    position: absolute;
    top: 5px;
    left: calc(-1 * var(--exp-pad) - 5px);
    width: 9px;
    height: 9px;
    background: var(--accent);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ExperienceCard = styled.div`
  text-align: left;
  width: 100%;
`;

const ExperienceRole = styled.div`
  font-size: clamp(17px, 2vw, 20px);
  font-weight: 700;
  color: var(--ink);
  font-family: var(--font-display);
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    line-height: 1.4;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const ExperiencePeriod = styled.span`
  display: block;
  margin-bottom: var(--space-xs);
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-3);
`;

const ExperienceDivider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--line-soft);
  margin: var(--space-md) 0;
`;

const ExperiencePhrases = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const ExperiencePhrase = styled.p`
  position: relative;
  margin: 0;
  padding-left: var(--space-md);
  max-width: 62ch;
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-2);
  font-family: var(--font-body);
  text-wrap: pretty;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 8px;
    height: 1px;
    background: var(--ink);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const ProjectPrivateNote = styled.p`
  margin: 0;
  max-width: 52ch;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.04em;
  color: var(--text-3);
  line-height: 1.5;
`;

/* ============================================================
   HABILIDADES & TECNOLOGIAS
   — sticker / comic brutal (eco do retrato 8px shadow)
   ============================================================ */
const SkillsSection = styled.section`
  width: 100%;
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--section-y) var(--container-x);
  scroll-margin-top: 96px;
  box-sizing: border-box;
`;

const SkillsTitle = styled.h2`
  ${sectionTitle}
  margin-bottom: var(--space-xl);

  @media (min-width: 900px) {
    margin-bottom: var(--space-2xl);
  }
`;

const SkillsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const SkillBlock = styled.article`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: var(--space-md);
  row-gap: var(--space-md);
  padding: var(--space-lg);
  border: 2px solid var(--line);
  background: var(--surface);
  box-shadow: 6px 6px 0 0 var(--accent);

  @media (min-width: 900px) {
    grid-template-columns: auto minmax(0, 40ch) minmax(0, 1fr);
    column-gap: clamp(var(--space-lg), 3vw, var(--space-2xl));
    padding: var(--space-xl);
    align-items: start;
  }

  html[data-theme="dark"] & {
    background: var(--bg-2);
    border-color: rgba(255, 255, 255, 0.42);
  }
`;

const SkillBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: var(--space-xs);
  border: 3px solid var(--ink);
  background: var(--accent);
  /* Ícone branco no tile, independente do tema. */
  color: #ffffff;
  box-shadow: 3px 3px 0 0 var(--ink);
  flex-shrink: 0;
  transition: transform 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out);

  svg {
    font-size: 26px;
    width: 1em;
    height: 1em;
  }

  @media (min-width: 900px) {
    width: 56px;
    height: 56px;

    svg {
      font-size: 28px;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    .skill-block:hover & {
      transform: translate(-2px, -2px);
      box-shadow: 5px 5px 0 0 var(--ink);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    .skill-block:hover & {
      transform: none;
    }
  }
`;

const SkillBlockMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const SkillBlockTitle = styled.h3`
  margin: 0;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(20px, 2.6vw, 28px);
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: var(--ink);
  text-wrap: balance;
`;

const SkillSummary = styled.p`
  margin: 0;
  max-width: 48ch;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.75;
  color: var(--text-2);
  text-wrap: pretty;
`;

const SkillPhrases = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  min-width: 0;

  @media (max-width: 899px) {
    grid-column: 1 / -1;
  }
`;

const SkillPhrase = styled.li`
  position: relative;
  margin: 0;
  padding: var(--space-sm) var(--space-md) var(--space-sm) calc(var(--space-md) + 8px);
  border: 1px solid var(--line-soft);
  background: var(--bg-2);
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text);
  font-family: var(--font-body);
  text-wrap: pretty;
  box-shadow: 2px 2px 0 0 var(--ink);

  &::before {
    content: "";
    position: absolute;
    left: var(--space-sm);
    top: calc(var(--space-sm) + 0.8em - 0.5px);
    width: 8px;
    height: 1px;
    background: var(--accent);
  }

  html[data-theme="dark"] & {
    background: #1c1c1c;
    border-color: rgba(255, 255, 255, 0.28);
    box-shadow: 2px 2px 0 0 rgba(255, 255, 255, 0.55);
  }
`;

const TechBlock = styled.div`
  margin-top: var(--section-y);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--line-soft);
`;

const TechGroupHeading = styled.h3`
  margin: 0 0 var(--space-sm);
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(22px, 3vw, 32px);
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: var(--ink);
  text-wrap: balance;
`;

const TechLead = styled.p`
  margin: 0 0 var(--space-xl);
  max-width: 52ch;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--text-2);
  text-wrap: pretty;
`;

const TechGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const TechGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const TechGroupLabel = styled.h4`
  margin: 0;
  font-family: var(--font-mono);
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-3);
`;

const SoftSkillList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
`;

const SoftSkillChip = styled.li`
  margin: 0;
  padding: 10px 14px;
  border: 1px solid var(--line);
  background: var(--bg-2);
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.3;
  color: var(--text);
  border-radius: 0;
  box-shadow: 3px 3px 0 0 var(--accent);
  cursor: default;
  transition: transform 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out),
    background-color 0.2s var(--ease-out);

  &:hover {
    transform: translate(-2px, -2px) rotate(1.5deg);
    box-shadow: 5px 5px 0 0 var(--ink);
    background: var(--accent-soft);
    color: var(--ink);
  }

  &:nth-child(odd):hover {
    transform: translate(-2px, -2px) rotate(-1.5deg);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background-color 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out);

    &:hover, &:nth-child(odd):hover {
      transform: none;
    }
  }
`;

/* ============================================================
   PROJETOS — layout editorial
   ============================================================ */
const ProjectsSection = styled.section`
  width: 100%;
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--section-y) var(--container-x);
  scroll-margin-top: 96px;
  box-sizing: border-box;
`;

const ProjectsTitle = styled.h2`
  ${sectionTitle}
  margin-bottom: var(--space-xl);

  @media (min-width: 900px) {
    margin-bottom: var(--space-2xl);
  }
`;

const ProjectShowcase = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-xl);

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: clamp(40px, 5vw, 72px);
    align-items: start;
  }
`;

const ProjectsGroupDivider = styled.div`
  width: 100%;
  height: 1px;
  margin: clamp(48px, 7vw, 80px) 0;
  background-color: var(--line);
`;

const ProjectIndexSticky = styled.div`
  @media (min-width: 900px) {
    position: sticky;
    top: 96px;
    align-self: start;
    z-index: 1;
  }
`;

const ProjectIndexList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--line);

  li {
    list-style: none;
  }
`;

const ProjectRowMarker = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  font-family: var(--font-mono);
  font-size: 18px;
  line-height: 1;
  color: var(--text-3);
  transition: transform 0.25s var(--ease-out), color 0.25s var(--ease-out);
`;

const ProjectRowIndexNum = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  color: inherit;
`;

const ProjectGroupTitle = styled.h3`
  margin: 0 0 12px;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
  color: var(--text-2);
`;

const ProjectGroupLead = styled.p`
  margin: 0 0 20px;
  max-width: 52ch;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--text-2);
  text-wrap: pretty;
`;

const ProjectRowName = styled.span`
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(22px, 3.4vw, 40px);
  letter-spacing: -0.04em;
  line-height: 1.05;
  color: inherit;
  text-wrap: balance;
  transition: transform 0.25s var(--ease-out);
`;

const ProjectRowNameBlock = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  transition: transform 0.25s var(--ease-out);
`;

const ProjectRowStatus = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-3);
  padding: 3px 8px;
  border: 1px solid var(--line);
  background: var(--bg-2);
`;

const ProjectInvestmentTag = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: var(--space-xs);
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--line);
  background: var(--bg-2);
  box-shadow: 3px 3px 0 0 var(--accent);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-2);
`;

const ProjectIndexRow = styled.button`
  appearance: none;
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: baseline;
  gap: var(--space-md);
  padding: clamp(16px, 2.2vw, 24px) 0;
  border: 0;
  border-bottom: 1px solid var(--line-soft);
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: var(--text-3);
  transition: color 0.25s var(--ease-out), background-color 0.25s var(--ease-out);

  &:hover,
  &[aria-current="true"],
  &[aria-expanded="true"] {
    color: var(--accent-text);
    background: var(--accent-soft);
  }

  &:hover ${ProjectRowMarker},
  &[aria-current="true"] ${ProjectRowMarker},
  &[aria-expanded="true"] ${ProjectRowMarker} {
    color: var(--accent);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover ${ProjectRowNameBlock},
    &[aria-current="true"] ${ProjectRowNameBlock} {
      transform: translateX(var(--space-sm));
    }

    &:hover ${ProjectRowMarker},
    &[aria-current="true"] ${ProjectRowMarker} {
      transform: translateX(4px);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    ${ProjectRowName},
    ${ProjectRowNameBlock},
    ${ProjectRowMarker} {
      transition: color 0.25s var(--ease-out);
    }
    &:hover ${ProjectRowName},
    &[aria-current="true"] ${ProjectRowName},
    &:hover ${ProjectRowMarker},
    &[aria-current="true"] ${ProjectRowMarker} {
      transform: none;
    }
  }
`;

const ProjectPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  min-width: 0;

  @media (min-width: 900px) {
    min-height: 920px;
  }
`;

const ProjectDetailCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  min-width: 0;

  @media (min-width: 900px) {
    gap: var(--space-lg);
  }
`;

const ProjectPreviewFade = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  min-width: 0;
  animation: projectFade 0.4s var(--ease-out) both;

  @keyframes projectFade {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (min-width: 900px) {
    gap: var(--space-lg);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProjectAccordionPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding: var(--space-md) 0 var(--space-xl);
  scroll-margin-top: 112px;
  scroll-margin-bottom: 64px;
  animation: projectFade 0.35s var(--ease-out) both;

  @keyframes projectFade {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProjectImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  order: 0;
  flex-shrink: 0;
  height: clamp(240px, 32vw, 320px);
  border: 1px solid var(--line);
  background: var(--surface);
  box-shadow: 6px 6px 0 0 var(--accent);
  border-radius: 2px 16px 2px 2px;
  transition: transform 0.3s var(--ease-out), box-shadow 0.3s var(--ease-out);

  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    &:hover {
      transform: translate(-2px, -3px);
      box-shadow: 9px 9px 0 var(--accent);
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.45s var(--ease-out);
  }

  &:hover img {
    transform: scale(1.03);
  }

  @media (min-width: 900px) {
    order: ${({ $reverse }) => ($reverse ? "1" : "0")};
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }
    &:hover img {
      transform: none;
    }
  }

  ${({ $logoThumbnail }) =>
    $logoThumbnail &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--bg-2);

      img {
        width: auto;
        height: auto;
        max-width: 50%;
        max-height: 60%;
        object-fit: contain;
      }

      &:hover img {
        transform: none;
      }
    `}
`;

const ProjectImageNote = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--space-sm) var(--space-md);
  background: var(--ink);
  color: var(--bg);
  font-size: 11px;
  font-family: var(--font-mono);
  letter-spacing: 0.04em;
  line-height: 1.4;
  text-align: left;
`;

const ProjectPartner = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: var(--space-sm);
  flex-wrap: wrap;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;

  span {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-3);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  ${({ $chip }) =>
    $chip &&
    css`
      padding: var(--space-xs) var(--space-sm);
      border: 1px solid var(--line);
      background: var(--bg-2);
      box-shadow: 3px 3px 0 0 var(--accent);

      span {
        color: var(--text-2);
      }
    `}
`;

const PartnerLogoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  transition: opacity 0.2s var(--ease-out);
  opacity: 0.85;
  text-decoration: none;
  border-radius: 0;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  img {
    height: 20px;
    width: auto;
  }

  span {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-2);
    letter-spacing: 0.04em;
    text-transform: none;
  }
`;

const ProjectDescription = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: var(--text-2);
  font-family: var(--font-body);
  margin: 0;
  max-width: 58ch;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  text-wrap: pretty;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ProjectActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-lg);
  width: 100%;
  margin-top: var(--space-xs);
`;

const ProjectTechLabel = styled.span`
  display: block;
  margin-bottom: var(--space-sm);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-3);
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 599px) {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    gap: var(--space-sm);

    ${({ $dualCode }) =>
      $dualCode &&
      css`
        grid-template-columns: 1fr 1fr;
      `}
  }
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
  min-height: 44px;
  padding: var(--space-sm) var(--space-lg);
  border: 1px solid var(--line);
  border-radius: 0;
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
    transform 0.15s var(--ease-out), box-shadow 0.15s var(--ease-out);

  svg {
    font-size: 16px;
    color: currentColor;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(1px);
  }

  ${({ $primary }) =>
    $primary
      ? css`
          border-width: 2px;
          background: var(--ink);
          color: var(--bg);
          box-shadow: 4px 4px 0 0 var(--accent);

          &:hover {
            transform: translate(-2px, -2px);
            background: var(--accent);
            color: #0a0a0a;
            border-color: var(--ink);
            box-shadow: 6px 6px 0 0 var(--ink);
          }
        `
      : css`
          background: transparent;
          color: var(--text);
          box-shadow: none;

          &:hover {
            background: var(--bg-2);
            color: var(--ink);
            border-color: var(--line);
          }
        `}

  ${({ $spanFull }) =>
    $spanFull &&
    css`
      @media (max-width: 599px) {
        grid-column: 1 / -1;
      }
    `}

  &&:active {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px 0 var(--ink);
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: var(--space-sm) var(--space-md);
    width: 100%;
    box-sizing: border-box;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
      box-shadow 0.2s var(--ease-out);

    &:hover,
    &&:active {
      transform: none;
    }
  }
`;

/* ============================================================
   CONTATO
   ============================================================ */
const ContactSection = styled.section`
  width: 100%;
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--section-y) var(--container-x);
  scroll-margin-top: 96px;
  box-sizing: border-box;
`;

const ContactTitle = styled.h2`
  ${sectionTitle}
`;

const ContactWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: stretch;
`;

const ContactPrimary = styled.a`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 16px 24px;
  border: 2px solid var(--ink);
  background: var(--ink);
  color: var(--bg);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(16px, 2vw, 20px);
  letter-spacing: -0.02em;
  text-decoration: none;
  box-shadow: 4px 4px 0 0 var(--accent);
  transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
    box-shadow 0.2s var(--ease-out), transform 0.15s var(--ease-out);

  svg {
    font-size: 20px;
    flex-shrink: 0;
    color: currentColor;
  }

  &:hover {
    background: var(--accent);
    color: #0a0a0a;
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 0 var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  &:active {
    transform: translate(3px, 3px);
    box-shadow: 1px 1px 0 var(--ink);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
      box-shadow 0.2s var(--ease-out);

    &:hover, &:active {
      transform: none;
    }
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  border-top: 1px solid var(--line);

  @media (min-width: 760px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ContactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 24px 0;
  border-bottom: 1px solid var(--line-soft);

  h3 {
    font-size: 13px;
    font-family: var(--font-body);
    font-weight: 600;
    color: var(--text-2);
    letter-spacing: -0.01em;
    text-transform: none;
    margin: 0;
  }

  a {
    font-size: clamp(15px, 1.8vw, 18px);
    font-family: var(--font-display);
    font-weight: 700;
    color: var(--ink);
    text-decoration: none;
    text-underline-offset: 5px;
    text-decoration-thickness: 2px;
    transition: color 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    word-break: break-word;
    letter-spacing: -0.02em;

    svg {
      font-size: 18px;
      color: var(--ink);
      flex-shrink: 0;
    }

    &:hover {
      color: var(--accent-text);
      text-decoration: underline;
    }
  }

  @media (min-width: 760px) {
    padding: 24px;

    &:not(:last-child) {
      border-right: 1px solid var(--line-soft);
    }
  }
`;

const ABOUT_LIFE_ICONS = [
  FaGamepad,
  FaDumbbell,
  FaCar,
  FaSeedling,
  FaFutbol,
  FaHorse,
  FaMusic,
  FaCoffee,
  MdOutdoorGrill,
  FaPizzaSlice,
  FaBolt,
  FaMountain,
  FaTree,
  GiCowboyBoot,
  FaHeadphones,
  FaLaptop,
  FaCamera,
  FaMobileAlt,
  FaClock,
  FaCat,
  FaMugHot,
];

/** PRNG estável → mesma sequência a cada rebuild da grade. */
const seededRandom = (seed) => {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

const ABOUT_ICON_SPACING = 40;

const AboutLifeField = () => {
  const rootRef = useRef(null);
  const [grid, setGrid] = useState({ cols: 0, rows: 0, icons: [] });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const build = () => {
      const { width, height } = root.getBoundingClientRect();
      if (width < 8 || height < 8) return;

      // Grade quadrada: mesma quantidade de colunas e linhas.
      const size = Math.min(width, height);
      const n = Math.max(1, Math.round(size / ABOUT_ICON_SPACING));
      const rand = seededRandom(14072026);
      const icons = Array.from({ length: n * n }, () => {
        const index = Math.floor(rand() * ABOUT_LIFE_ICONS.length);
        return ABOUT_LIFE_ICONS[index];
      });

      setGrid({ cols: n, rows: n, icons });
    };

    build();
    const observer = new ResizeObserver(build);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  return (
    <AboutIconFieldRoot
      ref={rootRef}
      aria-hidden="true"
      style={{
        gridTemplateColumns: grid.cols > 0 ? `repeat(${grid.cols}, 1fr)` : undefined,
        gridTemplateRows: grid.rows > 0 ? `repeat(${grid.rows}, 1fr)` : undefined,
      }}
    >
      {grid.icons.map((Icon, cellIndex) => (
        <AboutIconCell key={cellIndex}>
          <Icon />
        </AboutIconCell>
      ))}
    </AboutIconFieldRoot>
  );
};

const SHOWCASE_QUERY = "(min-width: 900px) and (hover: hover) and (pointer: fine)";

const LandingPage = ({ motionReady = true }) => {
  const pageRef = useRef(null);
  useReveal(pageRef, motionReady);
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [projectShowcase, setProjectShowcase] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(SHOWCASE_QUERY).matches
  );
  const [activeProject, setActiveProject] = useState(0);
  const [activeStudyProject, setActiveStudyProject] = useState(0);
  const [openProjectName, setOpenProjectName] = useState("SeatHub");
  const [openStudyName, setOpenStudyName] = useState("");
  const dropdownRef = useRef(null);
  const previousOpenProjectRef = useRef(openProjectName);

  useEffect(() => {
    if (previousOpenProjectRef.current === openProjectName) return;
    previousOpenProjectRef.current = openProjectName;
    if (!openProjectName) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => {
      const panel = document.getElementById(`project-panel-${openProjectName}`);
      if (!panel) return;
      panel.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "center",
        inline: "nearest",
      });
    }, reduceMotion ? 0 : 90);

    return () => window.clearTimeout(timer);
  }, [openProjectName]);

  useEffect(() => {
    if (!openStudyName) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => {
      const panel = document.getElementById(`study-panel-${openStudyName}`);
      if (!panel) return;
      panel.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "center",
        inline: "nearest",
      });
    }, reduceMotion ? 0 : 90);

    return () => window.clearTimeout(timer);
  }, [openStudyName]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) return undefined;

    let frame = 0;
    const narrowQuery = window.matchMedia("(max-width: 899px)");

    const update = () => {
      frame = 0;
      if (media.matches) {
        document.querySelectorAll("[data-parallax]").forEach((el) => {
          el.style.removeProperty("transform");
          delete el.dataset.parallaxY;
        });
        return;
      }
      const mid = window.innerHeight * 0.5;
      const isNarrow = narrowQuery.matches;

      document.querySelectorAll("[data-parallax]").forEach((el) => {
        const speed = Number(el.getAttribute("data-parallax"));
        if (!Number.isFinite(speed) || speed === 0) return;

        // No mobile, só camadas decorativas (absolute) — nunca seções em fluxo.
        if (isNarrow && el.getAttribute("data-parallax-layer") !== "decor") {
          el.style.removeProperty("transform");
          return;
        }

        const prevY = Number(el.dataset.parallaxY) || 0;
        const rect = el.getBoundingClientRect();
        const untransformedCenter = rect.top - prevY + rect.height * 0.5;

        if (
          untransformedCenter + rect.height < -160 ||
          untransformedCenter - rect.height > window.innerHeight + 160
        ) {
          return;
        }

        const y = (mid - untransformedCenter) * speed;
        el.dataset.parallaxY = String(y);
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      });
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    narrowQuery.addEventListener("change", onScroll);
    media.addEventListener("change", onScroll);
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      narrowQuery.removeEventListener("change", onScroll);
      media.removeEventListener("change", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(SHOWCASE_QUERY);
    const handleChange = (event) => setProjectShowcase(event.matches);

    setProjectShowcase(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const skillPillars = [
    {
      key: "frontend",
      Icon: FaLaptop,
      items: ["reusableComponents", "performanceUx", "serviceIntegrations"],
    },
    {
      key: "backend",
      Icon: FaServer,
      items: ["endpointsIntegrations", "businessRules", "authAccessControl"],
    },
    {
      key: "excellence",
      Icon: FaCode,
      items: ["codeReview", "documentation", "teamwork"],
    },
  ];

  const techCategories = [
    {
      key: "frontend",
      items: [
        "react",
        "reactnative",
        "html",
        "css",
        "js",
        "styledcomponents",
        "typescript",
      ],
    },
    {
      key: "backend",
      items: [
        "node",
        "java",
        "springboot",
        "python",
        "sql",
        "postgresql",
        "restful",
        "oauth2",
      ],
    },
    {
      key: "other",
      items: [
        "firebase",
        "supabase",
        "aws",
        "googlecloud",
        "stripe",
        "git",
        "github",
      ],
    },
  ];

  const softSkills = t("tech.softSkills.items", { returnObjects: true });

  const hasProjectLinks = (project) =>
    Boolean(project.deploy || project.github || project.githubBe);

  const productionProjects = [
    {
      name: "SeatHub",
      image: "Logo_seathub.png",
      descriptionKey: "projects.items.seatHub.description",
      github: "",
      deploy: "https://seathub.net",
      technologies: ["react", "js", "java", "springboot"],
      partner: {
        name: "Elevate",
        logo: "https://elevatebr.org/images/elevate-logo.png",
        url: "https://elevatebr.org/"
      }
    },
    {
      name: "TapInOut",
      image: "Logo_tapinout.png",
      descriptionKey: "projects.items.tapInOut.description",
      github: "",
      deploy: "https://tapinout.com",
      technologies: ["react", "js", "java", "springboot"],
      partner: {
        name: "Elevate",
        logo: "https://elevatebr.org/images/elevate-logo.png",
        url: "https://elevatebr.org/"
      }
    },
    {
      name: "Elevate Auth API",
      image: "https://elevatebr.org/images/elevate-logo.png",
      descriptionKey: "projects.items.elevateAuthApi.description",
      logoThumbnail: true,
      github: "",
      deploy: "",
      partner: {
        name: "Elevate",
        logo: "https://elevatebr.org/images/elevate-logo.png",
        url: "https://elevatebr.org/",
      },
      technologies: ["java", "springboot", "openid", "jwt", "docker", "git"],
    },
    {
      name: "JK Distribuidora",
      image: "Logo_acsdistribuidora.png",
      descriptionKey: "projects.items.jkDistribuidora.description",
      logoThumbnail: true,
      github: "",
      deploy: "https://acsdistribuidora.netlify.app",
      technologies: ["html", "css", "js"],
    },
    {
      name: "VendaJK CRM",
      image: "Logo_acsdistribuidora.png",
      descriptionKey: "projects.items.vendaJk.description",
      logoThumbnail: true,
      github: "",
      deploy: "https://vendajk.netlify.app",
      technologies: ["react", "js", "node", "postgresql", "supabase"],
    },
    {
      name: "GMK Software",
      image: "Logo_gmk.png",
      descriptionKey: "projects.items.gmkSoftware.description",
      logoThumbnail: true,
      github: "",
      deploy: "https://gmkagency.com",
      technologies: ["react", "js"],
    },
    {
      name: "AGGI - Lida",
      image: "Logo_aggi.png",
      descriptionKey: "projects.items.aggiLida.description",
      logoThumbnail: true,
      github: "",
      deploy: "https://agassist.netlify.app",
      technologies: ["react", "js", "node", "asaas", "whatsapp"],
    },
  ];

  const studyProjects = [
    {
      name: "CampoLead",
      image: `${process.env.PUBLIC_URL || ""}/Design sem nome (36).png`,
      descriptionKey: "projects.items.campoLead.description",
      noteKey: "projects.items.campoLead.statusNote",
      github: "https://github.com/JoaoZ14/CampoLead",
      githubBe: "https://github.com/JoaoZ14/CampoLead.be",
      deploy: "",
      technologies: ["reactnative", "js", "java", "springboot", "supabase", "restful", "git"],
    },
    {
      name: "Artilheiro Store",
      image: `${process.env.PUBLIC_URL || ""}/ArtilheiroStore.png`,
      descriptionKey: "projects.items.footballJerseyShop.description",
      noteKey: "projects.items.footballJerseyShop.inactivityNote",
      github: "https://github.com/JoaoZ14/ArtilheiroStore",
      githubBe: "https://github.com/JoaoZ14/ArtilheiroStore.be",
      deploy: "https://artilheirostore.netlify.app/",
      technologies: ["react", "js", "java", "springboot", "postgresql", "supabase", "stripe", "git"],
    },
    {
      name: "Busca CEP",
      image: "BuscaCep.png",
      descriptionKey: "projects.items.buscaCep.description",
      github: "https://github.com/JoaoZ14/BuscaCep",
      deploy: "https://searcep.netlify.app/",
      technologies: ["react", "js", "css", "html"],
    },
    {
      name: "Weather App",
      image: "WeatherApp.png",
      descriptionKey: "projects.items.weatherApp.description",
      noteKey: "projects.items.weatherApp.inactivityNote",
      github: "https://github.com/JoaoZ14/Weather-App",
      deploy: "https://weatherappjg.netlify.app",
      technologies: ["react", "html", "css", "js"],
    },
    {
      name: "PythonChat",
      image: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
      descriptionKey: "projects.items.pythonChat.description",
      logoThumbnail: true,
      github: "https://github.com/JoaoZ14/PythonChat",
      deploy: "",
      technologies: ["python", "git"],
    },
    {
      name: "Calculadora",
      image: "calculadora.png",
      descriptionKey: "projects.items.calculator.description",
      github: "https://github.com/JoaoZ14/Calculator",
      deploy: "",
      technologies: ["html", "css", "js"],
    },
  ];

  const activeProjectData = productionProjects[activeProject] || productionProjects[0];
  const activeStudyData = studyProjects[activeStudyProject] || studyProjects[0];

  const renderProjectDetails = (project, index) => {
    const hasLinks = hasProjectLinks(project);
    const hasDualCode = Boolean(project.github && project.githubBe);

    return (
      <ProjectDetailCard>
        <ProjectImageWrapper $logoThumbnail={project.logoThumbnail}>
          <img
            src={project.image}
            alt={project.name}
            loading={index < 2 ? "eager" : "lazy"}
            decoding="async"
          />
          {project.noteKey && (
            <ProjectImageNote>{t(project.noteKey)}</ProjectImageNote>
          )}
        </ProjectImageWrapper>

        {project.partner ? (
          <ProjectPartner $chip>
            <span>{t("projects.inCollaborationWith")}</span>
            <PartnerLogoLink
              href={project.partner.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("projects.visitPartnerSiteAria", { name: project.partner.name })}
            >
              <img
                src={project.partner.logo}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
              />
              <span>{project.partner.name}</span>
            </PartnerLogoLink>
          </ProjectPartner>
        ) : (
          <ProjectPartner>
            <span>{t("projects.personal")}</span>
          </ProjectPartner>
        )}

        {project.seekingInvestment && (
          <ProjectInvestmentTag>{t("projects.seekingInvestment")}</ProjectInvestmentTag>
        )}

        <ProjectDescription>{t(project.descriptionKey)}</ProjectDescription>

        {!hasLinks && (
          <ProjectPrivateNote>
            {t(project.privateNoteKey || "projects.privateNote")}
          </ProjectPrivateNote>
        )}

        {(project.technologies.length > 0 || hasLinks || project.seekingInvestment) && (
          <ProjectActions>
            {(hasLinks || project.seekingInvestment) && (
              <ProjectLinks $dualCode={hasDualCode}>
                {project.deploy && (
                  <ProjectLink
                    $primary
                    $spanFull={hasDualCode}
                    href={project.deploy}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("projects.viewSiteAria", { name: project.name })}
                  >
                    {t("projects.viewSite")}
                  </ProjectLink>
                )}
                {project.seekingInvestment && (
                  <ProjectLink
                    href={`https://wa.me/5524988685043?text=${encodeURIComponent(
                      t("projects.investmentWhatsappMessage", { name: project.name })
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("projects.investmentCtaAria", { name: project.name })}
                  >
                    <FaWhatsappIcon aria-hidden="true" /> {t("projects.investmentCta")}
                  </ProjectLink>
                )}
                {project.github && (
                  <ProjectLink
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("projects.viewCodeAria", { name: project.name })}
                  >
                    <FaGithubIcon aria-hidden="true" /> {project.githubBe ? t("projects.codeFrontend") : t("projects.code")}
                  </ProjectLink>
                )}
                {project.githubBe && (
                  <ProjectLink
                    href={project.githubBe}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("projects.viewCodeAria", { name: project.name })}
                  >
                    <FaGithubIcon aria-hidden="true" /> {t("projects.codeBackend")}
                  </ProjectLink>
                )}
              </ProjectLinks>
            )}

            {project.technologies.length > 0 && (
              <div>
                <ProjectTechLabel>{t("projects.stack")}</ProjectTechLabel>
                <TechTags items={project.technologies} />
              </div>
            )}
          </ProjectActions>
        )}
      </ProjectDetailCard>
    );
  };

  const handleDownloadCV = (language) => {
    const cvPaths = {
      pt: "/Curriculo%20Jo%C3%A3o%20Guilherme-PORTUGUES.pdf",
      en: "/Curriculo%20Jo%C3%A3o%20Guilherme-ENGLISH.pdf",
      es: "/Curriculo%20Jo%C3%A3o%20Guilherme-ESPANHOL.pdf",
    };

    window.open(`${process.env.PUBLIC_URL}${cvPaths[language]}`, "_blank");
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  return (
    <Wraper ref={pageRef}>
      <DivPrincipal>
        <DivMargin id="home" />

        <DivText>
          <HeroDotsLayer>
            <HeroDotField />
          </HeroDotsLayer>
          <HeroWatermark data-parallax="0.38" data-parallax-layer="decor" aria-hidden="true">
            João<br />Possidonio
          </HeroWatermark>
          <HeroSnippet aria-hidden="true">
            <span className="paper-depth" />
            <span className="cmt">{"// developer.js"}</span>
            {"\n"}
            <span className="kw">const</span>
            {" developer = {\n"}
            {"  "}
            <span className="key">name</span>
            <span className="punct">: </span>
            <span className="str">"João Possidonio"</span>
            <span className="punct">,</span>
            {"\n"}
            {"  "}
            <span className="key">role</span>
            <span className="punct">: </span>
            <span className="str">"fullstack"</span>
            <span className="punct">,</span>
            {"\n"}
            {"  "}
            <span className="key">base</span>
            <span className="punct">: </span>
            <span className="str">"Brasil"</span>
            <span className="punct">,</span>
            {"\n"}
            {"  "}
            <span className="key">openToWork</span>
            <span className="punct">: </span>
            <span className="bool">true</span>
            {"\n"}
            <span className="punct">{"};"}</span>
          </HeroSnippet>
          <HeroInner data-parallax="0.08">
            <HeroCopy data-reveal="hero">
              <HeroLead>
                <HeroPrint aria-hidden="true">
                  <span className="obj">console</span>
                  <span className="punct">.</span>
                  <span className="fn">log</span>
                  <span className="punct">(</span>
                  <span className="str">"Hello World"</span>
                  <span className="punct">);</span>
                </HeroPrint>
                <TextFirst>
                  {t("hero.brand")
                    .split(/\s+/)
                    .map((part, index) => (
                      <React.Fragment key={part}>
                        {index > 0 && <br />}
                        {part}
                      </React.Fragment>
                    ))}
                </TextFirst>
              </HeroLead>
              <TextSecond>{t("hero.role")}</TextSecond>
              <HeroMeta>
                <img
                  src="https://flagcdn.com/w40/br.png"
                  srcSet="https://flagcdn.com/w80/br.png 2x"
                  width="18"
                  height="12"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                />
                {t("hero.meta")}
              </HeroMeta>
            </HeroCopy>

            <HeroActions data-reveal="actions">
              <DivIcons>
                <a
                  href="https://wa.me/5524988685043"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("contact.whatsapp")}
                >
                  <FaWhatsappIcon />
                </a>

                <a
                  href="https://github.com/JoaoZ14"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("contact.github")}
                >
                  <FaGithubIcon />
                </a>

                <a
                  href="https://www.linkedin.com/in/joao-possidonio"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("contact.linkedin")}
                >
                  <FaLinkedinIcon />
                </a>

                <a
                  href="https://www.instagram.com/_possidonioj/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("contact.instagram")}
                >
                  <FaInstagramIcon />
                </a>
              </DivIcons>

              <ButtonCVContainer ref={dropdownRef} onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setShowDropdown(false);
                  dropdownRef.current?.querySelector("button")?.focus();
                }
              }}>
                <ButtonCV onClick={toggleDropdown} aria-expanded={showDropdown} aria-controls="cv-languages">
                  {t("hero.cv")}
                  <ArrowIcon $isOpen={showDropdown} />
                </ButtonCV>
                <DropdownMenu id="cv-languages" $isOpen={showDropdown}>
                  <DropdownItem onClick={() => handleDownloadCV("pt")}>
                    {t("hero.portuguese")}
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDownloadCV("en")}>
                    {t("hero.english")}
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDownloadCV("es")}>
                    {t("hero.spanish")}
                  </DropdownItem>
                </DropdownMenu>
              </ButtonCVContainer>
            </HeroActions>
          </HeroInner>
        </DivText>

        <SectionProfile id="about">
          <AboutSection>
            <AboutContent>
              <AboutBlocks>
                <AboutBlock $withLifeField>
                  <AboutSectionTitle $span data-reveal>
                    {t("about.title")}
                  </AboutSectionTitle>
                  <AboutCopy data-reveal>
                    <AboutSectionText>
                      {t("about.summary1")}
                    </AboutSectionText>
                    <AboutSectionText>
                      {t("about.summary2")}
                    </AboutSectionText>
                  </AboutCopy>
                  <AboutMedia data-reveal="media">
                    <AboutLifeField />
                    <AboutPortrait>
                      <img
                        src={`${process.env.PUBLIC_URL || ""}/joao-portrait.jpeg`}
                        alt="João Guilherme Possidonio"
                        loading="lazy"
                        decoding="async"
                      />
                    </AboutPortrait>
                  </AboutMedia>
                </AboutBlock>

                <AboutBlock id="experience">
                  <ExperienceTitle>
                    {t("about.experienceTitle")}
                  </ExperienceTitle>
                  <CompanyHeader>
                    <CompanyLogo
                      src="https://elevatebr.org/images/elevate-logo.png"
                      alt="Logo da elevate"
                      loading="eager"
                    />
                    <CompanyName>elevate</CompanyName>
                  </CompanyHeader>
                  <CompanyDescription>
                    {t("experience.elevate.description")}
                  </CompanyDescription>

                  <ExperienceTimeline>
                    <ExperienceTimelineItem>
                      <ExperienceCard>
                        <ExperiencePeriod>{t("experience.elevate.roles.junior.period")}</ExperiencePeriod>
                        <ExperienceRole>{t("experience.elevate.roles.junior.title")}</ExperienceRole>

                        <ExperienceDivider />
                        <ExperiencePhrases>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.productEvolution")}
                          </ExperiencePhrase>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.techLead")}
                          </ExperiencePhrase>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.fullCycleDelivery")}
                          </ExperiencePhrase>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.featuresFrontendBackend")}
                          </ExperiencePhrase>
                        </ExperiencePhrases>
                      </ExperienceCard>
                    </ExperienceTimelineItem>

                    <ExperienceTimelineItem>
                      <ExperienceCard>
                        <ExperiencePeriod>{t("experience.elevate.roles.intern.period")}</ExperiencePeriod>
                        <ExperienceRole>{t("experience.elevate.roles.intern.title")}</ExperienceRole>

                        <ExperienceDivider />
                        <ExperiencePhrases>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.endToEndImprovements")}
                          </ExperiencePhrase>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.architectureBestPractices")}
                          </ExperiencePhrase>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.bugFixes")}
                          </ExperiencePhrase>
                          <ExperiencePhrase>
                            {t("experience.elevate.bullets.reviewsDocumentation")}
                          </ExperiencePhrase>
                        </ExperiencePhrases>
                      </ExperienceCard>
                    </ExperienceTimelineItem>
                  </ExperienceTimeline>
                </AboutBlock>
              </AboutBlocks>
            </AboutContent>
          </AboutSection>
        </SectionProfile>

        <Line />

        <SkillsSection id="skills" aria-labelledby="skills-title">
          <SkillsTitle id="skills-title" data-reveal>{t("skills.title")}</SkillsTitle>

          <SkillsStack>
            {skillPillars.map((pillar) => {
              const PillarIcon = pillar.Icon;
              return (
                <SkillBlock key={pillar.key} className="skill-block" data-reveal>
                  <SkillBadge aria-hidden="true">
                    <PillarIcon />
                  </SkillBadge>
                  <SkillBlockMain>
                    <SkillBlockTitle>
                      {t(`skills.${pillar.key}.title`)}
                    </SkillBlockTitle>
                    <SkillSummary>
                      {t(`skills.${pillar.key}.summary`)}
                    </SkillSummary>
                  </SkillBlockMain>
                  <SkillPhrases>
                    {pillar.items.map((itemKey) => (
                      <SkillPhrase key={itemKey}>
                        {t(`skills.${pillar.key}.items.${itemKey}`)}
                      </SkillPhrase>
                    ))}
                  </SkillPhrases>
                </SkillBlock>
              );
            })}
          </SkillsStack>

          <TechBlock>
            <TechGroupHeading id="tech-title">{t("tech.title")}</TechGroupHeading>
            <TechLead>{t("tech.lead")}</TechLead>

            <TechGroups>
              {techCategories.map((category) => (
                <TechGroup key={category.key}>
                  <TechGroupLabel>
                    {t(`tech.categories.${category.key}`)}
                  </TechGroupLabel>
                  <TechTags items={category.items} />
                </TechGroup>
              ))}

              <TechGroup>
                <TechGroupLabel>
                  {t("tech.categories.softSkills")}
                </TechGroupLabel>
                <SoftSkillList>
                  {Array.isArray(softSkills) &&
                    softSkills.map((skill) => (
                      <SoftSkillChip key={skill}>{skill}</SoftSkillChip>
                    ))}
                </SoftSkillList>
              </TechGroup>
            </TechGroups>
          </TechBlock>
        </SkillsSection>

        <Line />

        <GitHubContributions />

        <Line />

        <ProjectsSection id="projects">
          <ProjectsTitle data-reveal>
            {t("projects.title")}
          </ProjectsTitle>

          {projectShowcase ? (
            <ProjectShowcase data-reveal="media">
              <ProjectIndexSticky>
                <ProjectGroupTitle>{t("projects.production")}</ProjectGroupTitle>
                <ProjectGroupLead>{t("projects.productionLead")}</ProjectGroupLead>
                <ProjectIndexList>
                  {productionProjects.map((project, index) => (
                    <li key={project.name}>
                      <ProjectIndexRow
                        type="button"
                        aria-current={activeProject === index}
                        aria-label={project.name}
                        onMouseEnter={() => setActiveProject(index)}
                        onFocus={() => setActiveProject(index)}
                        onClick={() => setActiveProject(index)}
                      >
                        <ProjectRowIndexNum>{String(index + 1).padStart(2, "0")}</ProjectRowIndexNum>
                        <ProjectRowNameBlock>
                          <ProjectRowName>{project.name}</ProjectRowName>
                          {project.seekingInvestment && (
                            <ProjectRowStatus>{t("projects.seekingInvestment")}</ProjectRowStatus>
                          )}
                        </ProjectRowNameBlock>
                        <ProjectRowMarker aria-hidden="true">→</ProjectRowMarker>
                      </ProjectIndexRow>
                    </li>
                  ))}
                </ProjectIndexList>
              </ProjectIndexSticky>

              <ProjectPreview aria-live="polite">
                <ProjectPreviewFade key={activeProjectData.name}>
                  {renderProjectDetails(activeProjectData, activeProject)}
                </ProjectPreviewFade>
              </ProjectPreview>
            </ProjectShowcase>
          ) : (
            <>
              <ProjectGroupTitle>{t("projects.production")}</ProjectGroupTitle>
              <ProjectGroupLead>{t("projects.productionLead")}</ProjectGroupLead>
              <ProjectIndexList as="div">
                {productionProjects.map((project, index) => {
                  const open = openProjectName === project.name;

                  return (
                    <div key={project.name}>
                      <ProjectIndexRow
                        type="button"
                        aria-expanded={open}
                        aria-controls={`project-panel-${project.name}`}
                        onClick={() => setOpenProjectName(open ? "" : project.name)}
                      >
                        <ProjectRowIndexNum>{String(index + 1).padStart(2, "0")}</ProjectRowIndexNum>
                        <ProjectRowNameBlock>
                          <ProjectRowName>{project.name}</ProjectRowName>
                          {project.seekingInvestment && (
                            <ProjectRowStatus>{t("projects.seekingInvestment")}</ProjectRowStatus>
                          )}
                        </ProjectRowNameBlock>
                        <ProjectRowMarker aria-hidden="true">{open ? "−" : "+"}</ProjectRowMarker>
                      </ProjectIndexRow>
                      {open && (
                        <ProjectAccordionPanel
                          id={`project-panel-${project.name}`}
                          role="region"
                          aria-label={project.name}
                        >
                          {renderProjectDetails(project, index)}
                        </ProjectAccordionPanel>
                      )}
                    </div>
                  );
                })}
              </ProjectIndexList>
            </>
          )}

          <ProjectsGroupDivider aria-hidden="true" />

          {projectShowcase ? (
            <ProjectShowcase>
              <ProjectIndexSticky>
                <ProjectGroupTitle>{t("projects.studies")}</ProjectGroupTitle>
                <ProjectGroupLead>{t("projects.studiesLead")}</ProjectGroupLead>
                <ProjectIndexList>
                  {studyProjects.map((project, index) => (
                    <li key={project.name}>
                      <ProjectIndexRow
                        type="button"
                        aria-current={activeStudyProject === index}
                        aria-label={project.name}
                        onMouseEnter={() => setActiveStudyProject(index)}
                        onFocus={() => setActiveStudyProject(index)}
                        onClick={() => setActiveStudyProject(index)}
                      >
                        <ProjectRowIndexNum>{String(index + 1).padStart(2, "0")}</ProjectRowIndexNum>
                        <ProjectRowNameBlock>
                          <ProjectRowName>{project.name}</ProjectRowName>
                          {project.seekingInvestment && (
                            <ProjectRowStatus>{t("projects.seekingInvestment")}</ProjectRowStatus>
                          )}
                        </ProjectRowNameBlock>
                        <ProjectRowMarker aria-hidden="true">→</ProjectRowMarker>
                      </ProjectIndexRow>
                    </li>
                  ))}
                </ProjectIndexList>
              </ProjectIndexSticky>

              <ProjectPreview aria-live="polite">
                <ProjectPreviewFade key={activeStudyData.name}>
                  {renderProjectDetails(activeStudyData, activeStudyProject)}
                </ProjectPreviewFade>
              </ProjectPreview>
            </ProjectShowcase>
          ) : (
            <>
              <ProjectGroupTitle>{t("projects.studies")}</ProjectGroupTitle>
              <ProjectGroupLead>{t("projects.studiesLead")}</ProjectGroupLead>
              <ProjectIndexList as="div">
                {studyProjects.map((project, index) => {
                  const open = openStudyName === project.name;

                  return (
                    <div key={project.name}>
                      <ProjectIndexRow
                        type="button"
                        aria-expanded={open}
                        aria-controls={`study-panel-${project.name}`}
                        onClick={() => setOpenStudyName(open ? "" : project.name)}
                      >
                        <ProjectRowIndexNum>{String(index + 1).padStart(2, "0")}</ProjectRowIndexNum>
                        <ProjectRowNameBlock>
                          <ProjectRowName>{project.name}</ProjectRowName>
                          {project.seekingInvestment && (
                            <ProjectRowStatus>{t("projects.seekingInvestment")}</ProjectRowStatus>
                          )}
                        </ProjectRowNameBlock>
                        <ProjectRowMarker aria-hidden="true">{open ? "−" : "+"}</ProjectRowMarker>
                      </ProjectIndexRow>
                      {open && (
                        <ProjectAccordionPanel
                          id={`study-panel-${project.name}`}
                          role="region"
                          aria-label={project.name}
                        >
                          {renderProjectDetails(project, index)}
                        </ProjectAccordionPanel>
                      )}
                    </div>
                  );
                })}
              </ProjectIndexList>
            </>
          )}
        </ProjectsSection>

        <Line />

        <ContactSection id="contact">
          <ContactTitle data-reveal>
            {t("contact.title")}
          </ContactTitle>

          <ContactWrapper data-reveal>
            <ContactPrimary
              href="https://wa.me/5524988685043"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsappIcon aria-hidden="true" />
              {t("contact.whatsapp")} · +55 (24) 98868-5043
            </ContactPrimary>

            <ContactInfo>
              <ContactItem>
                <h3>{t("contact.email")}</h3>
                <a href="mailto:joaopossidonio.dev@gmail.com">
                  joaopossidonio.dev@gmail.com
                </a>
              </ContactItem>

              <ContactItem>
                <h3>{t("contact.linkedin")}</h3>
                <a href="https://www.linkedin.com/in/joao-possidonio/" target="_blank" rel="noopener noreferrer">
                  <FaLinkedinIcon /> /joao-possidonio
                </a>
              </ContactItem>

              <ContactItem>
                <h3>{t("contact.github")}</h3>
                <a href="https://github.com/JoaoZ14" target="_blank" rel="noopener noreferrer">
                  <FaGithubIcon /> /JoaoZ14
                </a>
              </ContactItem>
            </ContactInfo>
          </ContactWrapper>
        </ContactSection>

        <Line />
      </DivPrincipal>
    </Wraper>
  );
};

export default LandingPage;
